/**
 * Maps a backend app state (or managed flag) to a friendly label and
 * semantic badge classes for use on the device-detail Installed / Managed
 * Apps tabs.
 *
 * The backend uses heterogeneous values across endpoints — `state`,
 * `installState`, `status` — so the helper accepts any string. Unknown
 * values fall through to the "muted" variant with the original string
 * (title-cased) as the label.
 */

export type AppStatusVariant = "success" | "warning" | "destructive" | "muted";

export interface AppStatusMeta {
  label: string;
  variant: AppStatusVariant;
  /** Tailwind classes for use on a <Badge> via className. */
  className: string;
}

const VARIANT_CLASSES: Record<AppStatusVariant, string> = {
  success: "bg-success-muted text-success border-transparent hover:bg-success-muted",
  warning: "bg-warning-muted text-warning border-transparent hover:bg-warning-muted",
  destructive: "bg-destructive/10 text-destructive border-transparent hover:bg-destructive/10",
  muted: "bg-muted text-muted-foreground border-transparent hover:bg-muted",
};

const titleCase = (s: string): string =>
  s
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

export function getAppStatusMeta(state?: string | null): AppStatusMeta {
  const raw = (state ?? "").toString().trim();
  if (!raw) {
    return { label: "Unknown", variant: "muted", className: VARIANT_CLASSES.muted };
  }
  const key = raw.toLowerCase().replace(/[_\s-]/g, "");

  // Success-y states
  if (["installed", "managed", "uptodate", "current"].includes(key)) {
    return { label: titleCase(raw), variant: "success", className: VARIANT_CLASSES.success };
  }
  // Warning / in-progress states
  if (
    [
      "pending",
      "tobeinstalled",
      "installing",
      "queued",
      "prompting",
      "needsredemption",
      "redeeming",
      "updating",
      "downloading",
    ].includes(key)
  ) {
    const labelMap: Record<string, string> = {
      tobeinstalled: "Pending",
      needsredemption: "Needs Redemption",
    };
    return {
      label: labelMap[key] ?? titleCase(raw),
      variant: "warning",
      className: VARIANT_CLASSES.warning,
    };
  }
  // Failure states
  if (
    [
      "failed",
      "failure",
      "error",
      "managementrejected",
      "userrejected",
      "rejected",
    ].includes(key)
  ) {
    return { label: titleCase(raw), variant: "destructive", className: VARIANT_CLASSES.destructive };
  }
  // Unmanaged / inactive
  if (["unmanaged", "unknown", "uninstalled", "removed"].includes(key)) {
    return { label: titleCase(raw), variant: "muted", className: VARIANT_CLASSES.muted };
  }

  return { label: titleCase(raw), variant: "muted", className: VARIANT_CLASSES.muted };
}

/** Convenience helper for the Installed Apps tab. */
export function getInstalledStatusMeta(managed?: boolean | null): AppStatusMeta {
  return getAppStatusMeta(managed ? "Managed" : "Unmanaged");
}
